// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Request Org Admin Access (AD-10, unified)
//
// Public page. Detects whether the named org already exists:
//   Path A (no match)    → creates org_signup_request (platform-admin review)
//   Path B (exact match) → creates org_admin_request (org admin + platform admin review)
//   Close match          → prompts user to confirm before routing

import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { callFunction, AppError } from '../services/firebase';
import { CloseMatchPrompt } from '../components/CloseMatchPrompt';
import { DomainMismatchModal } from '../components/DomainMismatchModal';

interface MatchResult {
  matchType: 'exact' | 'close' | 'none';
  orgId?:     string;
  orgName?:   string;
  candidates?: Array<{ orgId: string; orgName: string }>;
}

export default function RequestOrgAccessPage() {
  const [orgName,         setOrgName]         = useState('');
  const [matchResult,     setMatchResult]     = useState<MatchResult | null>(null);
  const [selectedOrgId,   setSelectedOrgId]   = useState<string | null>(null);
  const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null);
  const [requesterName,   setRequesterName]   = useState('');
  const [requesterEmail,  setRequesterEmail]  = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [domainWarning,   setDomainWarning]   = useState<{ orgDomain: string } | null>(null);
  const [domainAcknowledged, setDomainAcknowledged] = useState(false);
  const [isChecking,      setIsChecking]      = useState(false);
  const [isSubmitting,    setIsSubmitting]    = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [submitted,       setSubmitted]       = useState(false);
  const [submittedPath,   setSubmittedPath]   = useState<'new_org' | 'existing_org' | null>(null);

  const handleOrgNameBlur = async () => {
    const trimmed = orgName.trim();
    if (!trimmed || trimmed.length < 2) return;
    setIsChecking(true);
    setMatchResult(null);
    setSelectedOrgId(null);
    setSelectedOrgName(null);
    setDomainWarning(null);
    setDomainAcknowledged(false);
    try {
      const result = await callFunction<MatchResult>('checkOrgNameMatch', { orgName: trimmed });
      setMatchResult(result);
      if (result.matchType === 'exact' && result.orgId) {
        setSelectedOrgId(result.orgId);
        setSelectedOrgName(result.orgName ?? null);
      }
    } catch {
      // Non-fatal — if the check fails, user proceeds down Path A
    } finally {
      setIsChecking(false);
    }
  };

  const handleEmailBlur = async () => {
    const activeOrgId = selectedOrgId;
    if (!activeOrgId || !requesterEmail.includes('@')) return;
    try {
      const result = await callFunction<{
        hasDomainConfigured: boolean;
        matches: boolean;
        orgDomain?: string;
      }>('checkOrgDomain', {
        orgId: activeOrgId,
        email: requesterEmail.trim().toLowerCase(),
      });
      if (result.hasDomainConfigured && !result.matches && result.orgDomain) {
        setDomainWarning({ orgDomain: result.orgDomain });
        setDomainAcknowledged(false);
      } else {
        setDomainWarning(null);
      }
    } catch {
      // Non-fatal
    }
  };

  const handleSelectCandidate = (orgId: string, name: string) => {
    setSelectedOrgId(orgId);
    setSelectedOrgName(name);
    setOrgName(name);
    setMatchResult({ matchType: 'exact', orgId, orgName: name });
    setDomainWarning(null);
    setDomainAcknowledged(false);
  };

  const handleNoneMatch = () => {
    setSelectedOrgId(null);
    setSelectedOrgName(null);
    setMatchResult({ matchType: 'none' });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!orgName.trim())          { setError('Organization name is required.'); return; }
    if (!requesterName.trim())    { setError('Your name is required.'); return; }
    if (!requesterEmail.trim())   { setError('Your email is required.'); return; }
    if (!roleDescription.trim())  { setError('Please describe your role or organization.'); return; }
    // If domain warning is showing, the modal must be dismissed first (button is disabled)

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await callFunction<{ success: boolean; path: string }>(
        'submitOrgAdminRequest',
        {
          orgName:                   orgName.trim(),
          requesterName:             requesterName.trim(),
          requesterEmail:            requesterEmail.trim().toLowerCase(),
          roleDescription:           roleDescription.trim(),
          matchedOrgId:              selectedOrgId ?? undefined,
          domainMismatchAcknowledged: domainAcknowledged,
        }
      );
      setSubmittedPath(result.path as 'new_org' | 'existing_org');
      setSubmitted(true);
    } catch (err) {
      setError((err as AppError).message ?? 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isExistingOrg  = !!selectedOrgId;
  const displayOrgName = selectedOrgName ?? orgName.trim();

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--color-secondary)',
      padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-primary)',
            letterSpacing: -2, marginBottom: 6 }}>Ap</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
            Aphilaty Admin
          </div>
        </div>

        <div style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius-lg)', padding: 28 }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
              <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Request received</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                {submittedPath === 'existing_org'
                  ? `Your request to join ${displayOrgName} as an admin has been submitted.
                     The existing admins and platform team will review it — you'll receive
                     an invite link by email once approved.`
                  : `Thanks — we'll review your request and follow up by email within a few
                     business days. Once approved, you'll receive an invite link to activate
                     your account.`}
              </p>
            </div>
          ) : (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
                {isExistingOrg
                  ? `Request admin access — ${displayOrgName}`
                  : 'Request access for your organization'}
              </h1>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 20,
                lineHeight: 1.5 }}>
                {isExistingOrg
                  ? 'Tell us about your role. The org admins and platform team will review.'
                  : 'Tell us about your organization. We\'ll set up admin access once approved.'}
              </p>

              {error && (
                <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label className="form-label" htmlFor="org-name">Organization name</label>
                  <input
                    id="org-name"
                    type="text"
                    className="form-input"
                    value={orgName}
                    onChange={e => {
                      setOrgName(e.target.value);
                      // Reset match state when user edits the name
                      if (matchResult) {
                        setMatchResult(null);
                        setSelectedOrgId(null);
                        setSelectedOrgName(null);
                        setDomainWarning(null);
                        setDomainAcknowledged(false);
                      }
                    }}
                    onBlur={handleOrgNameBlur}
                    placeholder="Krueger Elementary PTA"
                    required
                  />
                  {isChecking && (
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      Checking…
                    </div>
                  )}
                  {matchResult?.matchType === 'exact' && (
                    <div style={{ fontSize: 11, color: 'var(--color-success)', marginTop: 4 }}>
                      Matched existing organization — your request will go to their admins for review.
                    </div>
                  )}
                </div>

                {matchResult?.matchType === 'close' && matchResult.candidates && (
                  <CloseMatchPrompt
                    candidates={matchResult.candidates}
                    onSelect={handleSelectCandidate}
                    onNoneMatch={handleNoneMatch}
                  />
                )}

                <div className="form-group">
                  <label className="form-label" htmlFor="req-name">Your name</label>
                  <input
                    id="req-name"
                    type="text"
                    className="form-input"
                    value={requesterName}
                    onChange={e => setRequesterName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="req-email">Your email</label>
                  <input
                    id="req-email"
                    type="email"
                    className="form-input"
                    value={requesterEmail}
                    onChange={e => {
                      setRequesterEmail(e.target.value);
                      setDomainWarning(null);
                      setDomainAcknowledged(false);
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="jane@example.com"
                    autoComplete="email"
                    required
                  />
                  {domainAcknowledged && (
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                      Domain mismatch acknowledged — reviewers will see a note about this.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="role-desc">
                    {isExistingOrg
                      ? 'Tell us about your role in this organization'
                      : 'Tell us about your organization'}
                  </label>
                  <textarea
                    id="role-desc"
                    className="form-input"
                    value={roleDescription}
                    onChange={e => setRoleDescription(e.target.value)}
                    placeholder={
                      isExistingOrg
                        ? 'E.g. "I\'m the PTA president for the 2026–27 school year…"'
                        : 'Briefly describe your group and how you\'d use Aphilaty…'
                    }
                    rows={3}
                    style={{ resize: 'vertical' }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: 8 }}
                  disabled={isSubmitting || (domainWarning !== null && !domainAcknowledged)}
                >
                  {isSubmitting ? <span className="spinner" /> : 'Submit Request'}
                </button>
              </form>
            </>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12,
          color: 'rgba(255,255,255,0.5)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>
            Sign in →
          </Link>
        </p>
      </div>

      {/* Domain mismatch modal — blocks submit until acknowledged or dismissed */}
      {domainWarning && !domainAcknowledged && (
        <DomainMismatchModal
          orgDomain={domainWarning.orgDomain}
          onConfirm={() => {
            setDomainAcknowledged(true);
            setDomainWarning(null);
          }}
          onGoBack={() => setDomainWarning(null)}
        />
      )}
    </div>
  );
}
